/* eslint-disable @typescript-eslint/no-explicit-any */
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';

import { profileService, type MyProfile, type MyRole } from '../../services/profile.service';
import EntityLink from '../../components/common/entity-link';
import { extractErrorMessage } from '../../utils/error-handler';

/** One claim out of the access token, without pulling in a JWT library. */
function decodeClaim(token: string | null, key: string): string | undefined {
  if (!token) return undefined;
  try {
    const seg = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
    return seg ? JSON.parse(atob(seg))?.[key] : undefined;
  } catch {
    return undefined;
  }
}

const DATE_TIME: Intl.DateTimeFormatOptions = {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
};

const absolute = (value?: string | null) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString(undefined, DATE_TIME);
};

/** "2 hours ago" — the form you actually read. Null when it says nothing useful. */
const relative = (value?: string | null) => {
  if (!value) return null;
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 0) return null;                       // clock skew — say nothing
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return days <= 30 ? `${days} day${days === 1 ? '' : 's'} ago` : null;
};

/** A role binding applies at one unit, or across the whole project. */
const scopeOf = (role: MyRole) =>
  role.isGlobal || !role.unitId ? 'Every unit in this project' : role.unitName || 'One unit';

/**
 * Section accents. Twelve identical grey icon tiles carried no information and
 * read as noise; giving each section its own tint makes the tile do a job —
 * you can tell at a glance which card a field belongs to.
 */
const ACCENT = {
  blue: { icon: 'text-blue-500', tile: 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400' },
  amber: { icon: 'text-amber-500', tile: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400' },
  violet: { icon: 'text-violet-500', tile: 'bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400' },
} as const;
type AccentName = keyof typeof ACCENT;

/** A single labelled field with an icon accent. */
const InfoRow = ({
  icon, label, value, accent = 'blue',
}: { icon: string; label: string; value: ReactNode; accent?: AccentName }) => (
  <div className="flex items-start gap-3">
    <span
      className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${ACCENT[accent].tile}`}
    >
      <i className={`${icon} text-sm`} />
    </span>
    <div className="min-w-0">
      <p className="text-xs text-ink-muted">{label}</p>
      <div className="break-words text-sm font-medium text-ink">
        {value === undefined || value === null || value === '' ? '—' : value}
      </div>
    </div>
  </div>
);

/** A card header: accent icon, title, optional count on the right, hairline under. */
const CardHead = ({
  icon, title, accent, aside,
}: { icon: string; title: string; accent: AccentName; aside?: ReactNode }) => (
  <h3 className="mb-4 flex items-center gap-2 border-b border-gray-100 pb-3 text-sm font-semibold text-gray-900 dark:border-gray-700/60 dark:text-white">
    <i className={`${icon} ${ACCENT[accent].icon}`} /> {title}
    {aside ? (
      <span className="ml-auto text-xs font-normal text-ink-muted">{aside}</span>
    ) : null}
  </h3>
);

const ProfileView = () => {
  const [profile, setProfile] = useState<MyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roles, setRoles] = useState<MyRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [switching, setSwitching] = useState<string | null>(null);

  // Which binding the current token is scoped to — the one role actually in force.
  const activeBindingId =
    (typeof window !== 'undefined' && localStorage.getItem('activeRoleBindingId')) ||
    decodeClaim(typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null,
      'roleBindingId');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setProfile(await profileService.getMe());
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Failed to fetch profile'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // A separate call and a separate failure: the profile is still worth showing
  // when the roles endpoint is unavailable.
  useEffect(() => {
    let mounted = true;
    profileService
      .getMyRoles()
      .then((r) => mounted && setRoles(r))
      .catch(() => mounted && setRoles([]))
      .finally(() => mounted && setRolesLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const switchTo = useCallback(async (roleBindingId: string) => {
    if (switching || roleBindingId === activeBindingId) return;
    setSwitching(roleBindingId);
    try {
      const res = await profileService.selectRole(roleBindingId);
      if (res?.accessToken) {
        localStorage.setItem('accessToken', res.accessToken);
        localStorage.setItem('activeRoleBindingId', res.roleBindingId);
        // Permissions, menu and every scoped list re-resolve under the new
        // binding — a reload is the honest way to get all of them at once.
        window.location.reload();
        return;
      }
    } catch {
      /* the button re-enables; the current role still holds */
    }
    setSwitching(null);
  }, [switching, activeBindingId]);

  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  if (error || !profile) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Profile not found'} className="mb-4 w-full" />
      </div>
    );
  }

  const displayName = profile.title
    ? `${profile.title} ${profile.name || ''}`.trim()
    : profile.name || '-';
  // Two letters, so two colleagues named R. don't both show "R".
  const initials = (profile.name || profile.email || 'U')
    .replace(/[._]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(-2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || 'U';
  const posting = [profile.designation?.name, profile.unit?.name].filter(Boolean).join(' · ');
  const account = profile.account || null;
  const lastLoginAbs = absolute(account?.lastLoginDateTime);
  const lastLoginRel = relative(account?.lastLoginDateTime);

  return (
    <div className=" mx-auto p-4" data-testid="SCR-Profile-View">
      <h1 className="text-lg font-semibold text-ink mb-3">My Profile</h1>

      {/* Hero card */}
      <div className="ds-card relative overflow-hidden mb-4">
        <div className="h-24 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600" />
        <div className="px-6 pb-5 -mt-12">
          {/* Avatar overlaps the banner */}
          <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md ring-4 ring-white dark:ring-gray-800">
            <span className="text-2xl font-bold tracking-tight text-white">{initials}</span>
          </div>
          {/* Name + contact sit BELOW the banner on a light surface (always readable) */}
          <div className="mt-3">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="break-words text-2xl font-bold tracking-tight text-ink">
                {displayName}
              </h2>
              <Tag
                value={profile.isOnDuty ? 'On Duty' : 'Off Duty'}
                severity={profile.isOnDuty ? 'success' : 'secondary'}
                className="text-xs"
              />
              {account?.blocked ? <Tag value="Blocked" severity="danger" className="text-xs" /> : null}
            </div>

            {/* The posting is what identifies an officer — it belongs with the name. */}
            {posting ? (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{posting}</p>
            ) : null}

            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-ink-muted">
              <span className="flex items-center gap-1.5">
                <i className="pi pi-envelope text-xs" /> {profile.email || '—'}
              </span>
              {profile.phoneNo && (
                <>
                  <span className="hidden h-3 w-px bg-gray-200 dark:bg-gray-600 sm:block" />
                  <span className="flex items-center gap-1.5">
                    <i className="pi pi-phone text-xs" /> {profile.phoneNo}
                  </span>
                </>
              )}
              {(account?.userId || profile.userId) && (
                <>
                  <span className="hidden h-3 w-px bg-gray-200 dark:bg-gray-600 sm:block" />
                  <span className="flex items-center gap-1.5">
                    <i className="pi pi-id-card text-xs" /> {account?.userId || profile.userId}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detail cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Account Information */}
        <section className="ds-card p-5">
          <CardHead icon="pi pi-user" title="Account Information" accent="blue" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoRow icon="pi pi-user" label="Full Name" value={displayName} />
            <InfoRow icon="pi pi-envelope" label="Email" value={profile.email} />
            <InfoRow icon="pi pi-phone" label="Phone Number" value={profile.phoneNo} />
            <InfoRow icon="pi pi-id-card" label="User ID" value={account?.userId || profile.userId} />
            <InfoRow
              icon="pi pi-check-circle"
              label="On Duty"
              value={
                <Tag
                  value={profile.isOnDuty ? 'Yes' : 'No'}
                  severity={profile.isOnDuty ? 'success' : 'secondary'}
                  className="text-xs"
                />
              }
            />
            <InfoRow
              icon="pi pi-sign-in"
              label="Last Login"
              value={
                lastLoginAbs ? (
                  <>
                    {lastLoginRel || lastLoginAbs}
                    {lastLoginRel ? (
                      <span className="block text-xs font-normal text-ink-muted">
                        {lastLoginAbs}
                      </span>
                    ) : null}
                  </>
                ) : (
                  'Never signed in'
                )
              }
            />
            <InfoRow
              icon="pi pi-shield"
              label="Account Status"
              value={
                <Tag
                  value={account?.blocked ? 'Blocked' : account?.userStatus || 'Active'}
                  severity={account?.blocked ? 'danger' : 'success'}
                  className="text-xs"
                />
              }
            />
          </div>
        </section>

        {/* Organization */}
        <section className="ds-card p-5">
          <CardHead icon="pi pi-building" title="Organization" accent="violet" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoRow
              icon="pi pi-building"
              label="Unit"
              value={
                <>
                  <EntityLink type="unit" id={(profile.unit as any)?._id} label={profile.unit?.name} />
                  {profile.unit?.unitCode ? (
                    <span className="text-xs text-ink-muted"> ({profile.unit.unitCode})</span>
                  ) : null}
                </>
              }
            />
            <InfoRow accent="violet" icon="pi pi-sitemap" label="Department" value={<EntityLink type="department" id={(profile.department as any)?._id} label={profile.department?.name} />} />
            <InfoRow accent="violet" icon="pi pi-flag" label="Ministry" value={<EntityLink type="ministry" id={(profile as any).ministry?._id} label={(profile as any).ministry?.name} />} />
            <InfoRow accent="violet" icon="pi pi-briefcase" label="Designation" value={<EntityLink type="designation" id={(profile.designation as any)?._id} label={profile.designation?.name} />} />
          </div>
        </section>

        {/* Roles & Access — which roles this user holds, and which is in force */}
        <section className="ds-card p-5 lg:col-span-2">
          <CardHead
            icon="pi pi-key"
            title="Roles & Access"
            accent="amber"
            aside={roles.length ? `${roles.length} ${roles.length === 1 ? 'role' : 'roles'}` : undefined}
          />

          {rolesLoading ? (
            <p className="m-0 py-4 text-sm text-ink-muted">Loading your roles…</p>
          ) : roles.length === 0 ? (
            <p className="m-0 py-4 text-sm text-ink-muted">
              No roles are assigned to you yet. An administrator grants access under
              Access Control → User Access.
            </p>
          ) : (
            <>
              <ul className="m-0 list-none divide-y divide-gray-100 p-0 dark:divide-gray-700/60">
                {roles.map((role) => {
                  const current = role.roleBindingId === activeBindingId;
                  return (
                    <li
                      key={role.roleBindingId}
                      className={`flex items-center gap-3 py-3 first:pt-0 last:pb-0 ${
                        current ? 'pl-3 -ml-3 border-l-2 border-amber-400 dark:border-amber-500' : ''
                      }`}
                    >
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                          current
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
                            : ACCENT.amber.tile
                        }`}
                      >
                        <i className="pi pi-key text-sm" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p
                          className={`m-0 text-sm text-ink ${
                            current ? 'font-semibold' : 'font-medium'
                          }`}
                        >
                          {role.roleName || role.roleKey || 'Role'}
                        </p>
                        <p className="m-0 mt-0.5 text-xs text-ink-muted">
                          {scopeOf(role)}
                          {role.inheritChildren ? ' · includes sub-units' : ''}
                          {role.description ? ` · ${role.description}` : ''}
                        </p>
                      </div>
                      <div className="shrink-0">
                        {current ? (
                          <Tag value="In use" severity="warning" className="text-xs" />
                        ) : (
                          <button
                            type="button"
                            onClick={() => switchTo(role.roleBindingId)}
                            disabled={!!switching}
                            data-testid={`Profile.SwitchRole.${role.roleBindingId}`}
                            className="text-xs font-semibold text-blue-600 hover:underline disabled:no-underline disabled:opacity-50 dark:text-blue-400"
                          >
                            {switching === role.roleBindingId ? 'Switching…' : 'Use this role'}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 text-xs text-ink-muted">
                A role is granted against one unit. The one marked <span className="font-medium">In use</span>{' '}
                is what the application filters everything by — switching changes that, and you keep the others.
              </p>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default ProfileView;
